import { CalendarPlus } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { Button } from '@/design-system/ui/Button';
import { DialogBody, DialogFooter } from '@/design-system/ui/Dialog';
import { DateField, TimeField } from '@/design-system/ui/DateField';
import { SimpleSelect } from '@/design-system/ui/Select';
import {
  occurrenceBaseLabel,
  occurrenceBases,
  occurrenceError,
  occurrenceTemplate,
} from '@escala/domain/occurrences';
import { quantityOf } from '@escala/domain/schedule';
import { useStore } from '@/store/AppStore';

/**
 * "Nova data de um evento" (no "Novo evento" e no "Nova data" do card de Eventos): o
 * evento único que já existe, a data e o horário. A data entra no mesmo evento, com os
 * ministérios e as quantidades da última data (`@escala/domain/occurrences`); para mudar
 * a configuração só desta data, edite-a depois.
 */
export default function NewOccurrence({
  seriesId: initialSeriesId,
  date: initialDate,
  header,
  onClose,
}: {
  seriesId: string | null;
  date: string;
  /** O que vem antes dos campos (a escolha entre evento novo e nova data). */
  header?: ReactNode;
  onClose: () => void;
}) {
  const { state, lookup, actions } = useStore();
  const bases = occurrenceBases(state.events);
  const [seriesId, setSeriesId] = useState(initialSeriesId ?? bases[0]?.seriesId ?? '');
  const template = occurrenceTemplate(state.events, seriesId);
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(template?.time ?? '');
  const [error, setError] = useState<string | null>(null);
  const conflict = seriesId ? occurrenceError(state.events, seriesId, date) : null;
  const shown = error ?? (date ? conflict : null);

  const choose = (id: string) => {
    setSeriesId(id);
    setTime(occurrenceTemplate(state.events, id)?.time ?? time);
    setError(null);
  };

  const save = () => {
    const refused = actions.addOccurrence(seriesId, date, time);

    if (refused) setError(refused);
    else onClose();
  };

  return (
    <>
      <DialogBody>
        {header}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="occurrence-event" className="text-small font-semibold">
            Evento
          </label>
          <SimpleSelect
            id="occurrence-event"
            value={seriesId}
            onValueChange={choose}
            options={bases.map((b) => ({ value: b.seriesId, label: occurrenceBaseLabel(b) }))}
          />
          <span className="text-caption text-ink-subtle">
            Para o evento que se repete sem data fixa, como um culto de batismo: a nova data entra no mesmo
            evento, com os ministérios e as funções dele. A escala é montada depois.
          </span>
        </div>

        <div className="grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-small font-semibold">Data</span>
            <DateField
              value={date}
              onChange={(next) => {
                setDate(next);
                setError(null);
              }}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-small font-semibold">Horário</span>
            <TimeField value={time} onChange={setTime} />
          </label>
        </div>

        {shown ? (
          <p role="alert" className="m-0 text-small text-status-conflict">
            {shown}
          </p>
        ) : null}

        {template ? (
          <div className="flex flex-col gap-2 rounded-block border border-line-input px-3 py-2.5">
            <span className="text-small font-semibold">Vem da última data ({template.title})</span>
            {template.ministryIds.map((id) => {
              const m = lookup.ministries.get(id);
              const roles = m?.roles.filter((r) => quantityOf(template, r) > 0) ?? [];

              return m ? (
                <div key={id} className="flex flex-col gap-0.5">
                  <span className="flex items-center gap-1.5 text-control font-semibold">
                    <span
                      aria-hidden
                      className="size-1.5 rounded-pill"
                      style={{ backgroundColor: m.color }}
                    />
                    {m.name}
                  </span>
                  <span className="pl-3 text-small text-ink-muted">
                    {roles.map((r) => `${r.name} (${quantityOf(template, r)})`).join(', ') ||
                      'Nenhuma função neste evento'}
                  </span>
                </div>
              ) : null;
            })}
            <span className="text-caption text-ink-subtle">
              Para mudar algo só nesta data, edite-a depois de criar.
            </span>
          </div>
        ) : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>
          Cancelar
        </Button>
        <Button onClick={save} disabled={!template || !date || !time || !!conflict}>
          <CalendarPlus aria-hidden />
          Adicionar data
        </Button>
      </DialogFooter>
    </>
  );
}
